// MCP servers that need you (docs/features/mcp-sign-in.md; Josh 10-09: "I did not get notified about needing to
// connect"): the Mac watches each running workspace's MCP servers through opencode's GET /mcp and says the ones that
// need a sign-in or can't connect — wire kind `mcp` (0x30 "0"): {t: 'mcp'} → {t: 'mcp', alerts: [{id, server,
// workspace, status, error?, pending?}]}, then each change. Home shows a card per alert; its button asks the Mac
// ({t: 'mcpPress', id, action} → {t: 'mcpPressed', id, ok, error?}): the sign-in opens in the Mac's browser, never
// here. The words are the Mac's (Shared/McpAlertWire.swift › McpAlertWords) and the watch's (McpLine.kt), held to
// tools/tests/mcp-lines.json.
//
// Two hooks, in app.js where a Mac's link comes up and in Home's press:
//   watchMcp(mac, link, repaint)     asks once, then keeps mac.mcp (and mac.mcpBusy, mac.mcpErrors) and repaints
//   pressMcp(mac, id, action, repaint)
import { registerWire } from './wire.js'

registerWire('mcp', (link, payload, message) => link.onMcp?.(message))

/// One alert, checked: {id, server, workspace, status, error?, pending?} or null.
export function readAlert(a) {
  if (!a || typeof a !== 'object' || typeof a.id !== 'string' || typeof a.server !== 'string' || typeof a.status !== 'string') return null
  return { id: a.id, server: a.server, workspace: typeof a.workspace === 'string' ? a.workspace : '', status: a.status,
    ...(typeof a.error === 'string' && a.error ? { error: a.error } : {}), ...(a.pending === true ? { pending: true } : {}) }
}

/// The alerts of a message (or of a glance's `mcp`): a list, or null when it says none at all.
export function readAlerts(message) {
  const list = message?.t === 'mcp' ? message.alerts : message
  return Array.isArray(list) ? list.map(readAlert).filter(Boolean) : null
}

const ERROR_LENGTH = 100
const needsSignIn = (a) => a.status === 'needs_auth'

/// One line, at most 100 characters, an ellipsis where it was cut (counted in characters, as Swift counts them).
export function clip(text) {
  const flat = String(text || '').split(/\s+/).filter(Boolean).join(' ')
  const chars = [...flat]
  return chars.length <= ERROR_LENGTH ? flat : chars.slice(0, ERROR_LENGTH - 1).join('') + '…'
}

export const title = (a) => (needsSignIn(a) ? `${a.server} needs you to sign in` : `${a.server} can't connect`)
export const short = (a) => (needsSignIn(a) ? `Sign in to ${a.server} on the Mac` : `${a.server} can't connect`)

export function detail(a) {
  if (a.pending) return `${a.workspace} · finish in the Mac's browser`
  const error = a.error ? clip(a.error) : ''
  return error ? `${a.workspace} · ${error}` : a.workspace
}

/// The card's buttons, [{id, label}]: on the Mac (`onMac`), or here, asking the Mac to press them.
export function actions(a, onMac = false) {
  if (needsSignIn(a)) {
    if (a.pending) return onMac ? [{ id: 'signIn', label: 'Open Again' }] : []
    return [{ id: 'signIn', label: onMac ? 'Sign In' : 'Sign in on the Mac' }]
  }
  return [{ id: 'retry', label: 'Retry' }, { id: 'restart', label: 'Restart Workspace' }]
}

/// The hook: this Mac's link is up. Asks once (an older Mac app never answers — no cards then).
export function watchMcp(mac, link, repaint) {
  mac.mcp = []
  mac.mcpBusy = new Set()
  mac.mcpErrors = new Map()
  link.onMcp = (message) => {
    if (message?.t === 'mcpPressed') {
      mac.mcpBusy.delete(message.id)
      if (message.ok === false && message.error) mac.mcpErrors.set(message.id, String(message.error))
      else mac.mcpErrors.delete(message.id)
      return repaint()
    }
    const next = readAlerts(message)
    if (!next) return
    const changed = JSON.stringify(next) !== JSON.stringify(mac.mcp)
    mac.mcp = next
    for (const id of [...mac.mcpErrors.keys()]) if (!next.some((a) => a.id === id)) mac.mcpErrors.delete(id)
    if (changed) repaint()
  }
  link.send('mcp', { t: 'mcp' })
}

/// A card's button: the Mac does it (its browser, its workspace); this card waits for its word.
export function pressMcp(mac, id, action, repaint = () => {}) {
  const link = mac?.windows
  if (!link) return false
  mac.mcpBusy?.add(id)
  mac.mcpErrors?.delete(id)
  link.send('mcp', { t: 'mcpPress', id, action })
  repaint()
  return true
}
